import type {
	ICheckboxField,
	ICurrencyField,
	IField,
	INotSupportField,
	InternalState,
	INumberField,
	IPercentField,
	ITextField,
} from '../types'

import { FieldType } from '../types'
import { CheckboxField } from './checkbox-field'
import { CurrencyField } from './currency-field'
import { Field } from './field'
import { NotSupportField } from './not-support-field'
import { NumberField } from './number-field'
import { PercentField } from './percent-field'
import { TextField } from './text-field'

export * from './field'
export * from './text-field'
export * from './number-field'
export * from './not-support-field'

export type IBindFieldModel = {
	(
		field: INumberField,
		state?: InternalState,
		newInstance?: boolean
	): NumberField
	(
		field: ICurrencyField,
		state?: InternalState,
		newInstance?: boolean
	): CurrencyField
	(
		field: IPercentField,
		state?: InternalState,
		newInstance?: boolean
	): PercentField
	(field: ITextField, state?: InternalState, newInstance?: boolean): TextField
	(
		field: INotSupportField,
		state?: InternalState,
		newInstance?: boolean
	): NotSupportField
	(
		field: ICheckboxField,
		state?: InternalState,
		newInstance?: boolean
	): CheckboxField
	(field: IField, state?: InternalState, newInstance?: boolean): Field
}

export type IBindFieldContext = {
	(field: INumberField, state: InternalState): NumberField
	(field: ICurrencyField, state: InternalState): CurrencyField
	(field: IPercentField, state: InternalState): PercentField
	(field: ITextField, state: InternalState): TextField
	(field: ICheckboxField, state: InternalState): CheckboxField
	(field: INotSupportField, state: InternalState): NotSupportField
	(field: IField, state: InternalState): Field
}

export const getFieldClass = (type: FieldType) => {
	switch (type) {
		case FieldType.Text: {
			return TextField
		}
		case FieldType.Number: {
			return NumberField
		}
		case FieldType.Percent: {
			return PercentField
		}
		case FieldType.Price:
		case FieldType.Currency: {
			return CurrencyField
		}
		case FieldType.Lock:
		case FieldType.Flag:
		case FieldType.Checkbox: {
			return CheckboxField
		}
		default: {
			return NotSupportField
		}
	}
}

/**
 * Bind the field model data to get an instance of the field calculation method.
 * A cache is done here. Prevent unnecessary memory consumption caused by new instances when called frequently.
 */
export const bindModel = (() => {
	const cache: Record<string, Field> = {}

	return (field: IField, inState?: InternalState, newInstance?: boolean) => {
		if (!inState) {
			throw new Error('Please pass in the store data source')
		}
		const state = inState
		// Force initialize a new instance
		if (newInstance) {
			const FieldClass = getFieldClass(field.type)

			return new FieldClass(field as any, state)
		}

		if (cache[field.type]) {
			// When the instance already exists, you only need to modify the this.field property to meet the data initialization requirements.
			cache[field.type]!.field = field
			cache[field.type]!.state = state

			return cache[field.type]
		}

		const FieldClass = getFieldClass(field.type)
		const fieldInstance = new FieldClass(field as any, state)
		cache[field.type] = fieldInstance as any

		return fieldInstance
	}
})()

function bindContext(field: INumberField, state: InternalState): NumberField
function bindContext(field: ITextField, state: InternalState): TextField
function bindContext(field: IPercentField, state: InternalState): PercentField
function bindContext(field: ICurrencyField, state: InternalState): CurrencyField
function bindContext(
	field: INotSupportField,
	state: InternalState
): NotSupportField
function bindContext(field: ICheckboxField, state: InternalState): CheckboxField
function bindContext(field: IField, state: InternalState): Field {
	const FieldClass = getFieldClass(field.type)

	return new FieldClass(field as any, state)
}

Field.bindModel = bindModel as IBindFieldModel
// The difference between bindContext and bindModel is that
// bindContext creates a new Field object every time, and it is mandatory to pass in the store.
Field.bindContext = bindContext as IBindFieldContext
